import React, { FC, useCallback } from 'react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../constants/aampeTheme';
import ICONS from '../../../constants/icons';

// components
import AampeTooltip from '../../shared/AampeTooltip/AampeTooltip';
// models
import { OptionModel } from './DropdownMenu';

interface DropdownOptionProps {
    option: OptionModel;
    handleAction: (action: () => void) => void;
    submenuOptions?: OptionModel[] | undefined;
    submenuPosition?: 'left' | 'right';
}

interface StyledProps {
    isDisabled?: boolean;
}

const DropdownOption: FC<React.PropsWithChildren<DropdownOptionProps>> = ({
    option,
    handleAction,
    submenuOptions,
    submenuPosition = 'right',
}) => {
    const [showSubmenu, setShowSubmenu] = React.useState(false);

    const hasSubmenu = submenuOptions && submenuOptions.length > 0;

    const onHover = useCallback(() => {
        if (hasSubmenu) {
            setShowSubmenu(true);
        }
    }, [hasSubmenu]);

    const onLeave = useCallback(() => {
        if (hasSubmenu) {
            setShowSubmenu(false);
        }
    }, [hasSubmenu]);
    const EditTypeRef = React.useRef<HTMLButtonElement>(null);

    return (
        <>
            <AampeTooltip
                title={option.tooltipText}
                padding={10}
                customZIndex={1300}
                arrow
            >
                <EditType
                    as={option.isLink ? 'a' : 'button'}
                    href={option.isLink ? option.url : undefined}
                    target={option.isLink ? '_blank' : undefined}
                    rel={option.isLink ? 'noopener noreferrer' : undefined}
                    ref={!option.isLink ? EditTypeRef : undefined}
                    isDisabled={option.isDisabled}
                    color={option.textColor}
                    textStyle={option.textStyle}
                    onMouseEnter={onHover}
                    onMouseLeave={onLeave}
                    title={
                        option.isDisabled && option.tooltipText
                            ? option.tooltipText
                            : undefined
                    }
                    onClick={(e: any) => {
                        if (option.isDisabled) return;
                        if (!option.isLink) {
                            e?.stopPropagation();
                            if (option.action) {
                                handleAction(option.action);
                            }
                        }
                    }}
                    submenuPosition={submenuPosition}
                >
                    {hasSubmenu && submenuPosition === 'left' && (
                        <ICONS.leftChevron />
                    )}
                    {!option.isTrailingIcon && option.icon}
                    {option.name}
                    {option.isTrailingIcon && option.icon}
                    {hasSubmenu && submenuPosition === 'right' && (
                        <ICONS.rightChevron />
                    )}
                </EditType>
            </AampeTooltip>
            {hasSubmenu && showSubmenu && (
                <SubmenuContainer
                    anchorRef={EditTypeRef}
                    submenuPosition={submenuPosition}
                    onMouseEnter={() => setShowSubmenu(true)}
                    onMouseLeave={() => setShowSubmenu(false)}
                >
                    {submenuOptions?.map((submenuOption, idx) => (
                        <DropdownOption
                            key={idx}
                            option={submenuOption}
                            handleAction={handleAction}
                            submenuOptions={submenuOption.submenuOptions}
                            submenuPosition={submenuPosition}
                        />
                    ))}
                </SubmenuContainer>
            )}
        </>
    );
};

export default DropdownOption;

const SubmenuContainer = styled.div<{
    anchorRef: React.RefObject<HTMLElement>;
    submenuPosition: 'left' | 'right';
}>`
    position: fixed;

    border-radius: 4px;
    border: 1px solid ${aampeTheme.colors.bordersWG01};
    background-color: ${aampeTheme.colors.white};

    /* Dropshadow */
    box-shadow: 0 4px 8px -1px rgba(20, 48, 76, 0.08),
        0 2px 4px -1px rgba(20, 48, 76, 0.04);
    min-width: 160px;
    display: flex;
    flex-direction: column;
    padding: ${aampeTheme.spacing(1)} 0;
    height: 50%;
    overflow: auto;
    /* Position the submenu relative to the parent */
    ${({ anchorRef, submenuPosition }) => {
        if (anchorRef.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            return `
                top: ${rect.top}px;
                left: ${
                    submenuPosition === 'right'
                        ? rect.right + 4
                        : rect.left - rect.width - 4
                }px;
            `;
        }
        return '';
    }}
`;

const EditType = styled.button.withConfig({
    shouldForwardProp: (prop) =>
        !['isDisabled', 'submenuPosition', 'textStyle'].includes(prop),
})<
    StyledProps & {
        submenuPosition?: 'left' | 'right';
        textStyle?: React.CSSProperties;
    }
>(({ isDisabled, color, submenuPosition, textStyle }) => ({
    display: 'flex',
    padding: aampeTheme.spacing(2),
    gap: aampeTheme.spacing(5),
    fontSize: aampeTheme.size.font.textMedium,
    fontFamily: 'Inclusive Sans Variable',
    weight: aampeTheme.weights.medium,
    color:
        color ??
        (isDisabled ? aampeTheme.colors.neutral09 : aampeTheme.colors.gray800),
    justifyContent: submenuPosition === 'left' ? 'flex-end' : 'space-between',
    textAlign: submenuPosition === 'left' ? 'right' : 'left',
    backgroundColor: isDisabled
        ? aampeTheme.colors.backgroundWG01
        : aampeTheme.colors.white,
    border: 'none',
    cursor: isDisabled ? 'normal' : 'pointer',
    textDecoration: 'none',
    outline: 'none',
    '&:hover': {
        color: isDisabled
            ? color
                ? color
                : aampeTheme.colors.neutral09
            : aampeTheme.colors.solidA01,
    },

    ...textStyle,
}));
